import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronsDown, ChevronsUp } from 'lucide-react';
import {
  api,
  EVENT_TYPE_SHORT,
  type AuthUser,
  type EventStatsHistoryLimit,
  type EventStatsRange,
  type EventStatsResponse,
  type EventStatsRow,
  type EventType,
  type RecentEventEntry,
  type SpecialEventStatsResponse,
  type SpecialEventStatsRow,
} from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import { MTypeIcon } from '@/components/MTypeIcon';
import { RecentEventsChip } from '@/components/RecentEventsChip';
import { EventsTabs } from './Events';

type TypeFilter = 'all' | EventType;
type SortKey =
  | 'name'
  | 'm1'
  | 'events'
  | 'regRate'
  | 'pickRate'
  | 'starter'
  | 'subRate'
  | 'noShowRate'
  | 'late';
type SortDir = 'asc' | 'desc';

const RANGE_OPTIONS: { value: EventStatsRange; label: string }[] = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];
const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'CS + DS' },
  { value: 'canyon_storm', label: 'Canyon Storm' },
  { value: 'desert_storm', label: 'Desert Storm' },
  { value: 'special_ingame', label: 'Special' },
];
const HISTORY_OPTIONS: EventStatsHistoryLimit[] = [5, 10, 20];

function pct(v: number | null): string {
  if (v == null) return '—';
  return `${Math.round(v * 100)}%`;
}

function specialSortValue(
  row: SpecialEventStatsRow,
  key: SortKey,
): number | string {
  switch (key) {
    case 'name':
      return (row.player.displayName || row.player.name).toLowerCase();
    case 'm1':
      return row.player.m1Power ?? -1;
    case 'events':
      return row.counts.events;
    case 'regRate':
      return row.rates.assignRate ?? -1;
    case 'pickRate':
      return row.rates.showRate ?? -1;
    case 'starter':
      return row.counts.showed;
    case 'subRate':
      return row.counts.rulebreaker;
    case 'noShowRate':
      return row.rates.noShowRate ?? -1;
    case 'late':
      return row.counts.rulebreaker;
  }
}

function sortValue(row: EventStatsRow, key: SortKey): number | string {
  switch (key) {
    case 'name':
      return (row.player.displayName || row.player.name).toLowerCase();
    case 'm1':
      return row.player.m1Power ?? -1;
    case 'events':
      return row.counts.events;
    case 'regRate':
      return row.rates.regRate ?? -1;
    case 'pickRate':
      return row.rates.pickRate ?? -1;
    case 'starter':
      return row.counts.starter;
    case 'subRate':
      return row.rates.subRate ?? -1;
    case 'noShowRate':
      return row.rates.noShowRate ?? -1;
    case 'late':
      return row.counts.late;
  }
}

export default function EventStats({ user }: { user: AuthUser }) {
  const [params, setParams] = useSearchParams();
  const type: TypeFilter =
    (params.get('type') as TypeFilter | null) ?? 'all';
  const range: EventStatsRange =
    (params.get('range') as EventStatsRange | null) ?? 'all';
  const activeOnly = params.get('activeOnly') !== '0';
  const historyLimit: EventStatsHistoryLimit =
    (Number(params.get('history')) as EventStatsHistoryLimit) === 10
      ? 10
      : (Number(params.get('history')) as EventStatsHistoryLimit) === 20
        ? 20
        : 5;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const [sortKey, setSortKey] = useState<SortKey>('regRate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [openRows, setOpenRows] = useState<Set<number>>(() => new Set());
  const toggleRow = (id: number) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };

  const isSpecial = type === 'special_ingame';
  const query = useQuery<EventStatsResponse | SpecialEventStatsResponse>({
    queryKey: ['event-stats', { type, range, activeOnly, historyLimit }],
    queryFn: () =>
      isSpecial
        ? api.getSpecialEventStats({ range, activeOnly, historyLimit })
        : api.getEventStats({ type, range, activeOnly, historyLimit }),
  });

  const allOpen = useMemo(() => {
    const list = query.data?.players ?? [];
    return list.length > 0 && list.every((r) => openRows.has(r.player.id));
  }, [query.data, openRows]);
  const toggleAll = () => {
    const list = query.data?.players ?? [];
    if (allOpen) setOpenRows(new Set());
    else setOpenRows(new Set(list.map((r) => r.player.id)));
  };

  const rows = useMemo(() => {
    if (isSpecial) return [] as EventStatsRow[];
    const list = ((query.data?.players ?? []) as EventStatsRow[]) ?? [];
    const sorted = [...list].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [query.data, sortKey, sortDir, isSpecial]);

  const specialRows = useMemo(() => {
    if (!isSpecial) return [] as SpecialEventStatsRow[];
    const list = ((query.data?.players ?? []) as SpecialEventStatsRow[]) ?? [];
    return [...list].sort((a, b) => {
      const av = specialSortValue(a, sortKey);
      const bv = specialSortValue(b, sortKey);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [query.data, sortKey, sortDir, isSpecial]);

  return (
    <div className="space-y-4">
      <EventsTabs user={user} />

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          value={type}
          options={TYPE_OPTIONS}
          onChange={(v) => setParam('type', v === 'all' ? null : v)}
        />
        <SegmentedControl
          value={range}
          options={RANGE_OPTIONS}
          onChange={(v) => setParam('range', v === 'all' ? null : v)}
        />
        <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-slate-800 bg-slate-900 px-3 py-1.5 text-sm">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setParam('activeOnly', e.target.checked ? null : '0')}
            className="accent-indigo-500"
          />
          Only active
        </label>
        <div className="inline-flex items-center gap-2 rounded border border-slate-800 bg-slate-900 px-3 py-1.5 text-sm">
          <span className="text-slate-400">History</span>
          {HISTORY_OPTIONS.map((n) => (
            <button
              key={n}
              onClick={() => setParam('history', String(n))}
              className={cn(
                'rounded px-2 py-0.5 text-xs',
                historyLimit === n
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:text-white',
              )}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-slate-400 tabular-nums">
          {query.data
            ? `${query.data.events.total} event${
                query.data.events.total === 1 ? '' : 's'
              }${
                query.data.events.from
                  ? ` · ${query.data.events.from} → ${query.data.events.to}`
                  : ''
              }`
            : ''}
        </div>
      </div>

      {query.isLoading ? (
        <div className="text-center text-slate-500">Loading…</div>
      ) : (query.data?.events.total ?? 0) === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-8 text-center text-slate-500">
          No finalized events in the selected range.
        </div>
      ) : isSpecial ? (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="w-8 px-1 py-2 text-center">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="inline-flex items-center justify-center rounded p-0.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                    title={allOpen ? 'Collapse all' : 'Expand all'}
                    aria-label={allOpen ? 'Collapse all' : 'Expand all'}
                  >
                    {allOpen ? <ChevronsUp size={14} /> : <ChevronsDown size={14} />}
                  </button>
                </th>
                <Th
                  label="Player"
                  k="name"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  className="text-left"
                />
                <Th
                  label="M1"
                  k="m1"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  hideOnMobile
                />
                <Th
                  label="Events"
                  k="events"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Assigned %"
                  k="regRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Showed %"
                  k="pickRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Rulebreaker"
                  k="subRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  hideOnMobile
                />
                <Th
                  label="No-show %"
                  k="noShowRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <th className="px-3 py-2 text-left font-medium">History</th>
              </tr>
            </thead>
            <tbody>
              {specialRows.map((row) => (
                <SpecialStatsRow
                  key={row.player.id}
                  row={row}
                  open={openRows.has(row.player.id)}
                  onToggle={() => toggleRow(row.player.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="w-8 px-1 py-2 text-center">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="inline-flex items-center justify-center rounded p-0.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                    title={allOpen ? 'Collapse all' : 'Expand all'}
                    aria-label={allOpen ? 'Collapse all' : 'Expand all'}
                  >
                    {allOpen ? <ChevronsUp size={14} /> : <ChevronsDown size={14} />}
                  </button>
                </th>
                <Th
                  label="Player"
                  k="name"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  className="text-left"
                />
                <Th
                  label="M1"
                  k="m1"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  hideOnMobile
                />
                <Th
                  label="Events"
                  k="events"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Reg %"
                  k="regRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Picked %"
                  k="pickRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Starter"
                  k="starter"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  hideOnMobile
                />
                <Th
                  label="Sub %"
                  k="subRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  hideOnMobile
                />
                <Th
                  label="No-show %"
                  k="noShowRate"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                />
                <Th
                  label="Late"
                  k="late"
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onClick={toggleSort}
                  hideOnMobile
                />
                <th className="px-3 py-2 text-left font-medium">History</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <StatsRow
                  key={row.player.id}
                  row={row}
                  historyLimit={historyLimit}
                  open={openRows.has(row.player.id)}
                  onToggle={() => toggleRow(row.player.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
  className,
  hideOnMobile,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
  className?: string;
  hideOnMobile?: boolean;
}) {
  const active = k === sortKey;
  return (
    <th
      className={cn(
        'select-none px-3 py-2 text-right font-medium',
        hideOnMobile && 'hidden md:table-cell',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onClick(k)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wider',
          active ? 'text-white' : 'hover:text-white',
        )}
      >
        {label}
        {active && <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded border border-slate-800 bg-slate-900 p-0.5 text-sm">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-3 py-1 transition',
            value === o.value
              ? 'bg-indigo-600 text-white'
              : 'text-slate-400 hover:text-white',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatsRow({
  row,
  historyLimit,
  open,
  onToggle,
}: {
  row: EventStatsRow;
  historyLimit: EventStatsHistoryLimit;
  open: boolean;
  onToggle: () => void;
}) {
  const c = row.counts;
  const r = row.rates;
  const label = row.player.displayName || row.player.name;
  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-t border-slate-800/60 hover:bg-slate-900/50',
          !row.player.active && 'opacity-60',
        )}
        onClick={onToggle}
      >
        <td className="px-2 py-2 text-slate-500">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{label}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {row.player.rank}
            </span>
            {!row.player.active && (
              <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">
                inactive
              </span>
            )}
          </div>
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
          {row.player.m1Power != null ? (
            <span
              className="inline-flex items-center gap-1"
              title={
                row.player.totalPower != null
                  ? `Total ${formatPower(row.player.totalPower)}`
                  : undefined
              }
            >
              {row.player.m1Type && (
                <MTypeIcon type={row.player.m1Type} size={12} />
              )}
              <span>{formatPower(row.player.m1Power)}</span>
            </span>
          ) : (
            <span className="text-slate-600">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{c.events}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          <RateCell count={c.registered} total={c.events} rate={r.regRate} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          <RateCell count={c.picked} total={c.registered} rate={r.pickRate} />
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
          {c.starter}
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
          <RateCell count={c.sub} total={c.picked} rate={r.subRate} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          <RateCell
            count={c.noShow}
            total={c.picked}
            rate={r.noShowRate}
            highlight={r.noShowRate != null && r.noShowRate >= 0.2}
          />
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
          {c.late}
        </td>
        <td className="px-3 py-2">
          <div onClick={(e) => e.stopPropagation()}>
            <RecentEventsChip
              events={row.history}
              collapsedCount={historyLimit}
              expandedCount={historyLimit}
              defaultOpen
            />
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-slate-800/60 bg-slate-950/60">
          <td></td>
          <td colSpan={10} className="px-3 py-3">
            <PlayerBreakdown row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function RateCell({
  count,
  total,
  rate,
  highlight,
}: {
  count: number;
  total: number;
  rate: number | null;
  highlight?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1',
        highlight && 'text-rose-300',
      )}
    >
      <span className="text-xs text-slate-400">
        {count}/{total}
      </span>
      <span className="w-10 text-right">{pct(rate)}</span>
    </span>
  );
}

function PlayerBreakdown({ row }: { row: EventStatsRow }) {
  const c = row.counts;
  const r = row.rates;
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <BreakdownBlock title="Registration">
        <BreakdownLine label="Registered" value={c.registered} rate={r.regRate} />
        <BreakdownLine label="Not registered" value={c.notRegistered} />
      </BreakdownBlock>
      <BreakdownBlock title="Selection">
        <BreakdownLine label="Picked" value={c.picked} rate={r.pickRate} />
        <BreakdownLine label="Not picked" value={c.notPicked} />
        <BreakdownLine label="Starter" value={c.starter} />
        <BreakdownLine label="Sub" value={c.sub} rate={r.subRate} />
      </BreakdownBlock>
      <BreakdownBlock title="Attendance">
        <BreakdownLine label="Showed" value={c.showed} />
        <BreakdownLine label="Late" value={c.late} />
        <BreakdownLine
          label="No-show"
          value={c.noShow}
          rate={r.noShowRate}
          danger={r.noShowRate != null && r.noShowRate >= 0.2}
        />
      </BreakdownBlock>
      <div className="md:col-span-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          History ({row.history.length} shown)
        </div>
        <ul className="grid gap-1 text-xs sm:grid-cols-2">
          {row.history.length === 0 ? (
            <li className="text-slate-500">No events in range.</li>
          ) : (
            row.history.map((e, i) => (
              <li
                key={`${e.eventDate}-${e.type}-${i}`}
                className="flex items-center gap-2 rounded bg-slate-900/60 px-2 py-1"
              >
                <span className="tabular-nums text-slate-400">{e.eventDate}</span>
                <span className="text-slate-500">
                  {EVENT_TYPE_SHORT[e.type]}
                  {e.slot ? ` · ${e.slot}` : ''}
                </span>
                <span className="ml-auto">{describeOutcome(e)}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function BreakdownBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </div>
      <div className="space-y-1 text-xs">{children}</div>
    </div>
  );
}

function BreakdownLine({
  label,
  value,
  rate,
  danger,
}: {
  label: string;
  value: number;
  rate?: number | null;
  danger?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-2',
        danger && 'text-rose-300',
      )}
    >
      <span className="text-slate-400">{label}</span>
      <span className="tabular-nums">
        <strong className="text-slate-100">{value}</strong>
        {rate !== undefined && (
          <span className="ml-1 text-slate-500">{pct(rate)}</span>
        )}
      </span>
    </div>
  );
}

function SpecialStatsRow({
  row,
  open,
  onToggle,
}: {
  row: SpecialEventStatsRow;
  open: boolean;
  onToggle: () => void;
}) {
  const c = row.counts;
  const r = row.rates;
  const label = row.player.displayName || row.player.name;
  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-t border-slate-800/60 hover:bg-slate-900/50',
          !row.player.active && 'opacity-60',
        )}
        onClick={onToggle}
      >
        <td className="px-2 py-2 text-slate-500">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{label}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {row.player.rank}
            </span>
            {!row.player.active && (
              <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">
                inactive
              </span>
            )}
          </div>
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
          {row.player.m1Power != null ? (
            <span
              className="inline-flex items-center gap-1"
              title={
                row.player.totalPower != null
                  ? `Total ${formatPower(row.player.totalPower)}`
                  : undefined
              }
            >
              {row.player.m1Type && (
                <MTypeIcon type={row.player.m1Type} size={12} />
              )}
              <span>{formatPower(row.player.m1Power)}</span>
            </span>
          ) : (
            <span className="text-slate-600">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{c.events}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          <RateCell count={c.assigned} total={c.events} rate={r.assignRate} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          <RateCell count={c.showed} total={c.assigned} rate={r.showRate} />
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
          {c.rulebreaker}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          <RateCell
            count={c.noShow}
            total={c.assigned}
            rate={r.noShowRate}
            highlight={r.noShowRate != null && r.noShowRate >= 0.2}
          />
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
            {row.history.length === 0 ? (
              <span className="text-xs text-slate-600">—</span>
            ) : (
              row.history.map((h) => (
                <SpecialSwatch key={h.eventId} entry={h} />
              ))
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-slate-800/60 bg-slate-950/60">
          <td></td>
          <td colSpan={8} className="px-3 py-3">
            <SpecialBreakdown row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function SpecialSwatch({
  entry,
}: {
  entry: SpecialEventStatsRow['history'][number];
}) {
  const tone = !entry.assigned
    ? 'bg-slate-800 text-slate-500'
    : entry.attendance === 'showed'
      ? 'bg-emerald-800/70 text-emerald-100'
      : entry.attendance === 'rulebreaker'
        ? 'bg-amber-800/70 text-amber-100'
        : entry.attendance === 'no_show'
          ? 'bg-rose-800/70 text-rose-100'
          : 'bg-indigo-900/60 text-indigo-100';
  const letter = !entry.assigned
    ? '·'
    : entry.attendance === 'showed'
      ? 'S'
      : entry.attendance === 'rulebreaker'
        ? 'R'
        : entry.attendance === 'no_show'
          ? 'N'
          : 'A';
  const teamPart =
    entry.assigned && entry.teamIndex != null
      ? ` · Team ${entry.teamIndex}`
      : '';
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded px-1 text-[10px] font-semibold',
        tone,
      )}
      title={`${entry.eventDate}${entry.customName ? ` · ${entry.customName}` : ''}${teamPart} · ${
        !entry.assigned
          ? 'not assigned'
          : entry.attendance ?? 'pending'
      }`}
    >
      {letter}
    </span>
  );
}

function SpecialBreakdown({ row }: { row: SpecialEventStatsRow }) {
  const c = row.counts;
  const r = row.rates;
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <BreakdownBlock title="Participation">
        <BreakdownLine label="Assigned" value={c.assigned} rate={r.assignRate} />
        <BreakdownLine label="Not assigned" value={c.notAssigned} />
      </BreakdownBlock>
      <BreakdownBlock title="Attendance">
        <BreakdownLine label="Showed" value={c.showed} rate={r.showRate} />
        <BreakdownLine label="Rulebreaker" value={c.rulebreaker} />
        <BreakdownLine
          label="No-show"
          value={c.noShow}
          rate={r.noShowRate}
          danger={r.noShowRate != null && r.noShowRate >= 0.2}
        />
        <BreakdownLine label="Pending" value={c.pending} />
      </BreakdownBlock>
      <div className="md:col-span-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Recent events ({row.history.length} shown)
        </div>
        <ul className="grid gap-1 text-xs sm:grid-cols-2">
          {row.history.length === 0 ? (
            <li className="text-slate-500">No events in range.</li>
          ) : (
            row.history.map((e) => (
              <li
                key={e.eventId}
                className="flex items-center gap-2 rounded bg-slate-900/60 px-2 py-1"
              >
                <span className="tabular-nums text-slate-400">{e.eventDate}</span>
                <span className="truncate text-slate-500">
                  {e.customName || 'Special'}
                  {e.teamIndex != null ? ` · Team ${e.teamIndex}` : ''}
                </span>
                <span className="ml-auto">
                  {!e.assigned ? (
                    <span className="text-slate-500">not assigned</span>
                  ) : e.attendance === 'showed' ? (
                    <span className="text-emerald-300">showed</span>
                  ) : e.attendance === 'rulebreaker' ? (
                    <span className="text-amber-300">rulebreaker</span>
                  ) : e.attendance === 'no_show' ? (
                    <span className="text-rose-300">no-show</span>
                  ) : (
                    <span className="text-slate-400">pending</span>
                  )}
                </span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function describeOutcome(e: RecentEventEntry): React.ReactNode {
  if (e.outcome === 'picked') {
    const parts = [e.role === 'sub' ? 'picked (sub)' : 'picked'];
    if (e.attendance === 'showed') parts.push('showed');
    else if (e.attendance === 'late') parts.push('late');
    else if (e.attendance === 'no_show') parts.push('no-show');
    else parts.push('pending');
    const isNoShow = e.attendance === 'no_show';
    // Colour matches the swatch: sub = sky-blue, starter = emerald,
    // no-show = rose (regardless of role).
    const tone = isNoShow
      ? 'text-rose-300'
      : e.role === 'sub'
        ? 'text-sky-300'
        : 'text-emerald-300';
    return <span className={cn(tone)}>{parts.join(' · ')}</span>;
  }
  if (e.outcome === 'applied_not_picked') {
    return (
      <span className={cn(e.redCarded ? 'text-rose-400' : 'text-red-300')}>
        {e.redCarded ? 'applied · red-carded' : 'not picked'}
      </span>
    );
  }
  return (
    <span className={cn(e.redCarded ? 'text-rose-400' : 'text-slate-400')}>
      {e.redCarded ? 'not registered · red-carded' : 'not registered'}
    </span>
  );
}
